import { useQueryManager } from "@/composables/useQueryManager";
import {
  isItemAvailable,
  isPartialCount,
} from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import type { FlattenedPortfolioItem, PortfolioItemTransfer } from "@/types";
import {
  computed,
  reactive,
  readonly,
  ref,
  triggerRef,
  watch,
  type Ref,
} from "vue";
import { useRoute } from "vue-router";

export type SelectionPolicy = (item: FlattenedPortfolioItem) => boolean;

export interface UsePortfolioSelectionOptions {
  /**
   * Predicate that decides which items are eligible for selection.
   * Defaults to {@link isItemAvailable} (status === "AVAILABLE").
   * Admin flows pass `() => true` to allow PENDING / RESERVED / etc. items.
   */
  policy?: SelectionPolicy;
  /**
   * When true, mirror selection into `?portItemIds=` and restore it from
   * the URL on mount. Intended for deep-linkable page-level selection.
   * Modals should leave this off so their ephemeral state does not leak
   * into the route or stomp the underlying page's selection.
   */
  syncToUrl?: boolean;
  /**
   * The rows to select from. Defaults to the shared {@link usePortfolioItemsStore} cache (the
   * signed-in company's own portfolio); a page listing other rows (e.g. Soldera's inventory) passes
   * its own so they never mix with the cache.
   */
  items?: Readonly<Ref<readonly FlattenedPortfolioItem[]>>;
}

/**
 * `?portItemIds=` format: a bare id selects the whole item, `id:count` part of it. Full rows stay bare
 * so the common all-full selection is as short as the old id-only format, which still parses.
 */
export const encodeSelectionParam = (
  selectedCounts: ReadonlyMap<number, number>,
  itemsMap: ReadonlyMap<number, FlattenedPortfolioItem>
): string =>
  [...selectedCounts.entries()]
    .sort(([a], [b]) => a - b)
    .map(([id, count]) =>
      isPartialCount(count, itemsMap.get(id)) ? `${id}:${count}` : String(id)
    )
    .join(",");

/** Parses `?portItemIds=` into id → requested count; `null` means the whole item. */
export const parseSelectionParam = (
  raw: string
): Map<number, number | null> => {
  const parsed = new Map<number, number | null>();
  for (const entry of raw.split(",")) {
    const [idPart, countPart] = entry.split(":");
    const id = Number(idPart);
    if (!idPart || !Number.isInteger(id)) continue;
    const count = countPart === undefined ? NaN : Number(countPart);
    parsed.set(id, Number.isInteger(count) && count > 0 ? count : null);
  }
  return parsed;
};

export interface PortfolioSelection {
  readonly selectedItemIds: ReadonlySet<number>;
  readonly selectedItems: FlattenedPortfolioItem[];
  readonly hasSelectedItems: boolean;
  readonly totalSelectedMwh: number;
  /** How many selected items hold only part of their amount (see {@link isPartialCount}). */
  readonly partialItemCount: number;
  readonly selectedItemsForApi: PortfolioItemTransfer[];
  readonly isSelectable: SelectionPolicy;
  /**
   * True once `syncToUrl` has restored a non-empty selection from `?portItemIds=` on first load —
   * i.e. the page opened with a selection carried over from another tab or a link.
   */
  readonly restoredFromUrl: boolean;
  selectItem(itemId: number): void;
  forceSelectItem(itemId: number): void;
  deselectItem(itemId: number): void;
  updateItemSelectedCount(itemId: number, count: number): void;
  getSelectedCount(itemId: number): number | undefined;
  clearSelection(): void;
  setSelectedItems(items: FlattenedPortfolioItem[]): void;
}

/**
 * Per-instance portfolio item selection state, decoupled from the shared
 * {@link usePortfolioItemsStore} so multiple components (e.g. a page-level
 * table and a modal) can hold independent selections concurrently.
 *
 * Selection is stored as `{itemId -> selectedCount}` in a local map; cache
 * items are looked up by id but never mutated. Callers control eligibility
 * via {@link UsePortfolioSelectionOptions.policy}.
 */
export function usePortfolioSelection(
  options: UsePortfolioSelectionOptions = {}
): PortfolioSelection {
  const { policy = isItemAvailable, syncToUrl = false, items } = options;
  const cache = items ? null : usePortfolioItemsStore();
  const allItems = computed(
    (): readonly FlattenedPortfolioItem[] =>
      items?.value ?? cache?.allItems ?? []
  );
  const itemsMap = computed((): ReadonlyMap<number, FlattenedPortfolioItem> =>
    cache ? cache.itemsMap : new Map(allItems.value.map((i) => [i.id, i]))
  );

  // Per-instance selection state. Each usePortfolioSelection() call gets its
  // own map, so multiple coexisting selections (e.g. a page-level table and a
  // modal's selection) don't collide via shared cache items.
  const selectedCounts = ref<Map<number, number>>(new Map());

  const selectedItemIds = computed<ReadonlySet<number>>(() => {
    return new Set(selectedCounts.value.keys());
  });

  const selectedItems = computed((): FlattenedPortfolioItem[] => {
    return allItems.value.filter((item) => selectedCounts.value.has(item.id));
  });

  const hasSelectedItems = computed(
    (): boolean => selectedCounts.value.size > 0
  );

  const totalSelectedMwh = computed((): number => {
    let total = 0;
    for (const count of selectedCounts.value.values()) total += count;
    return total;
  });

  const partialItemCount = computed(
    (): number =>
      [...selectedCounts.value].filter(([id, count]) =>
        isPartialCount(count, itemsMap.value.get(id))
      ).length
  );

  const selectedItemsForApi = computed((): PortfolioItemTransfer[] => {
    const result: PortfolioItemTransfer[] = [];
    for (const [id, selected_count] of selectedCounts.value.entries()) {
      const item = itemsMap.value.get(id);
      if (!item) continue;
      result.push({ id, count: item.count, selected_count });
    }
    return result;
  });

  function getSelectedCount(itemId: number): number | undefined {
    return selectedCounts.value.get(itemId);
  }

  function selectItem(itemId: number): void {
    if (selectedCounts.value.has(itemId)) return;
    const item = itemsMap.value.get(itemId);
    if (item && policy(item)) {
      selectedCounts.value.set(itemId, item.count);
      triggerRef(selectedCounts);
    }
  }

  function forceSelectItem(itemId: number): void {
    if (selectedCounts.value.has(itemId)) return;
    const item = itemsMap.value.get(itemId);
    if (item) {
      selectedCounts.value.set(itemId, item.count);
      triggerRef(selectedCounts);
    }
  }

  function deselectItem(itemId: number): void {
    if (selectedCounts.value.delete(itemId)) {
      triggerRef(selectedCounts);
    }
  }

  function updateItemSelectedCount(itemId: number, count: number): void {
    const item = itemsMap.value.get(itemId);
    if (!item) return;
    if (!policy(item)) return;

    const validCount = Math.max(0, Math.min(count, item.count));
    if (validCount > 0) {
      selectedCounts.value.set(itemId, validCount);
    } else {
      selectedCounts.value.delete(itemId);
    }
    triggerRef(selectedCounts);
  }

  function clearSelection(): void {
    if (selectedCounts.value.size > 0) {
      selectedCounts.value.clear();
      triggerRef(selectedCounts);
    }
  }

  // Reconcile selection state when the underlying cache changes (e.g. after
  // a refetch following a save). Items that disappeared must be dropped, and
  // counts must be clamped to the new item.count — otherwise the UI keeps
  // showing stale totals while the API payload silently omits the entries,
  // and edge-case payloads would fail backend validation (selected_count >
  // count).
  watch(itemsMap, (newMap) => {
    let changed = false;
    for (const [id, count] of Array.from(selectedCounts.value.entries())) {
      const item = newMap.get(id);
      if (!item) {
        selectedCounts.value.delete(id);
        changed = true;
      } else if (count > item.count) {
        selectedCounts.value.set(id, item.count);
        changed = true;
      }
    }
    if (changed) triggerRef(selectedCounts);
  });

  function setSelectedItems(items: FlattenedPortfolioItem[]): void {
    const newIds = new Set(items.map((i) => i.id));
    let changed = false;

    for (const id of Array.from(selectedCounts.value.keys())) {
      if (!newIds.has(id)) {
        selectedCounts.value.delete(id);
        changed = true;
      }
    }

    for (const item of items) {
      if (!selectedCounts.value.has(item.id)) {
        selectedCounts.value.set(item.id, item.count);
        changed = true;
      }
    }

    if (changed) triggerRef(selectedCounts);
  }

  const restoredFromUrl = ref(false);

  if (syncToUrl) {
    const route = useRoute();
    const { updateQuery } = useQueryManager();

    // Watch the encoded string rather than the ids so a count-only change (a partial amount) reaches
    // the URL too. The table commits counts on Enter/blur, so this never fires per keystroke.
    const encodedSelection = computed(() =>
      encodeSelectionParam(selectedCounts.value, itemsMap.value)
    );

    watch(
      encodedSelection,
      (encoded) => {
        updateQuery({ portItemIds: encoded || undefined });
      },
      { flush: "post" }
    );

    // Track whether the cache has been populated at least once so the initial
    // hydration runs after items load, not on the empty cache present on mount.
    let initialHydrationDone = false;

    const syncFromUrl = () => {
      const raw = route.query.portItemIds;
      if (typeof raw === "string") {
        if (raw === encodedSelection.value) return;
        applyUrlSelection(parseSelectionParam(raw));
      } else if (raw === undefined || raw === null) {
        clearSelection();
      }
    };

    // One batched update: restores each item's count clamped to [1, item.count], skipping items the
    // policy rejects, and drops selected items the URL no longer lists.
    const applyUrlSelection = (requested: Map<number, number | null>) => {
      const next = new Map<number, number>();
      for (const [id, count] of requested) {
        const item = itemsMap.value.get(id);
        if (!item || !policy(item)) continue;
        next.set(id, Math.min(count ?? item.count, item.count));
      }
      selectedCounts.value = next;
    };

    watch(() => route.query.portItemIds, syncFromUrl);

    // Deep links land before cache.fetchItems resolves, so the route watcher
    // above can't restore selection on first load — itemsMap is empty when it
    // fires. Defer the initial hydration to the first non-empty cache.
    watch(
      itemsMap,
      (newMap) => {
        if (initialHydrationDone) return;
        if (newMap.size === 0) return;
        initialHydrationDone = true;
        syncFromUrl();
        restoredFromUrl.value = selectedCounts.value.size > 0;
      },
      { immediate: true }
    );
  }

  return reactive({
    selectedItemIds,
    selectedItems,
    hasSelectedItems,
    totalSelectedMwh,
    partialItemCount,
    selectedItemsForApi,
    isSelectable: policy,
    restoredFromUrl: readonly(restoredFromUrl),
    selectItem,
    forceSelectItem,
    deselectItem,
    updateItemSelectedCount,
    getSelectedCount,
    clearSelection,
    setSelectedItems,
  }) as PortfolioSelection;
}
